import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ArrowRightIcon, SparklesIcon, CheckCircle2Icon } from 'lucide-react';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { UpcomingBill } from '../../constants/mockData';
export interface BillSelectionScreenProps {
  bills: UpcomingBill[];
  onSelectBill: (bill: UpcomingBill) => void;
  onCancel?: () => void;
}
export function BillSelectionScreen({
  bills,
  onSelectBill,
  onCancel
}: BillSelectionScreenProps) {
  const getStatusColor = (daysUntilDue: number) => {
    if (daysUntilDue < 0) return '#C62828'; // Overdue - Red
    if (daysUntilDue === 0) return '#E65100'; // Due today - Orange
    if (daysUntilDue <= 3) return '#F57C00'; // Due soon - Amber
    return OPENFINANCE_THEME.colors.success; // Green
  };
  const getStatusText = (daysUntilDue: number) => {
    if (daysUntilDue < 0) return 'Overdue';
    if (daysUntilDue === 0) return 'Due today';
    if (daysUntilDue === 1) return 'Due tomorrow';
    return `Due in ${daysUntilDue} days`;
  };
  // Find the recommended bill (du - the one without VRP)
  const recommendedBill = bills.find(b => !b.hasVRP);
  return <AnimatedScreen data-id="element-302">
      <div className="space-y-6" data-id="element-303">
        {/* Header */}
        <div data-id="element-304">
          <div className="flex items-center gap-2 mb-3" data-id="element-305">
            <motion.div animate={{
            rotate: [0, 360]
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'linear'
          }} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
            background: `linear-gradient(135deg, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent600} 100%)`
          }} data-id="element-306">
              <SparklesIcon className="w-4 h-4 text-white" data-id="element-307" />
            </motion.div>
            <h2 className="text-xl font-bold leading-tight" style={{
            fontFamily: OPENFINANCE_THEME.fonts.display,
            color: OPENFINANCE_THEME.colors.primary700
          }} data-id="element-308">
              Choose a Bill to Automate
            </h2>
          </div>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-309">
            Select which recurring bill you'd like to set up auto-pay for. We'll
            help you configure payment rules and authorize the setup.
          </p>
        </div>

        {/* Benefits Banner */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="p-4 rounded-xl" style={{
        background: OPENFINANCE_THEME.gradients.card,
        border: `2px solid ${OPENFINANCE_THEME.colors.primary200}`
      }} data-id="element-310">
          <div className="flex items-start gap-3" data-id="element-311">
            <CheckCircle2Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-312" />
            <div data-id="element-313">
              <div className="text-sm font-semibold mb-1" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-314">
                Why Auto-Pay?
              </div>
              <div className="text-xs leading-relaxed" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-315">
                Never miss a payment • Avoid late fees • Save time every month •
                Cancel anytime
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bill Cards */}
        <motion.div variants={staggerContainer} initial="hidden" animate="show" className="space-y-3" data-id="element-316">
          {bills.map(bill => {
          const isRecommended = bill.id === recommendedBill?.id;
          const statusColor = getStatusColor(bill.daysUntilDue);
          return <motion.button key={bill.id} variants={staggerItem} onClick={() => onSelectBill(bill)} className="w-full text-left p-4 rounded-xl border-2 transition-all hover:shadow-lg active:scale-[0.98] relative overflow-hidden" style={{
            borderColor: isRecommended ? OPENFINANCE_THEME.colors.accent500 : bill.hasVRP ? OPENFINANCE_THEME.colors.success + '40' : OPENFINANCE_THEME.colors.border,
            backgroundColor: isRecommended ? OPENFINANCE_THEME.colors.accent500 + '08' : 'white'
          }} data-id="element-317">
                {/* Recommended Badge */}
                {isRecommended && <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold" style={{
              backgroundColor: OPENFINANCE_THEME.colors.accent500,
              color: 'white'
            }} data-id="element-318">
                    Recommended
                  </div>}

                {/* Already Has Auto-Pay Badge */}
                {bill.hasVRP && <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1" style={{
              backgroundColor: OPENFINANCE_THEME.colors.success + '15',
              color: OPENFINANCE_THEME.colors.success,
              border: `1px solid ${OPENFINANCE_THEME.colors.success}40`
            }} data-id="element-319">
                    <CheckCircle2Icon className="w-3 h-3" data-id="element-320" />
                    <span data-id="element-321">Auto-pay active</span>
                  </div>}

                <div className="flex items-start gap-3" data-id="element-322">
                  {/* Merchant Logo */}
                  {bill.merchantLogo && <div className="w-14 h-14 rounded-xl flex items-center justify-center p-2 flex-shrink-0" style={{
                backgroundColor: OPENFINANCE_THEME.colors.borderLight
              }} data-id="element-323">
                      <img src={bill.merchantLogo} alt={bill.merchantName} className="w-full h-full object-contain" data-id="element-324" />
                    </div>}

                  {/* Bill Info */}
                  <div className="flex-1 min-w-0" data-id="element-325">
                    <div className="font-bold text-base mb-1" style={{
                  color: OPENFINANCE_THEME.colors.text,
                  fontFamily: OPENFINANCE_THEME.fonts.heading
                }} data-id="element-326">
                      {bill.merchantName}
                    </div>

                    <div className="flex items-center gap-2 mb-2" data-id="element-327">
                      <CalendarIcon className="w-3.5 h-3.5" style={{
                    color: statusColor
                  }} data-id="element-328" />
                      <span className="text-xs font-semibold" style={{
                    color: statusColor
                  }} data-id="element-329">
                        {getStatusText(bill.daysUntilDue)}
                      </span>
                    </div>

                    <div className="flex items-baseline gap-2" data-id="element-330">
                      <span className="text-2xl font-bold" style={{
                    color: OPENFINANCE_THEME.colors.text,
                    fontFamily: OPENFINANCE_THEME.fonts.heading
                  }} data-id="element-331">
                        {bill.currency} {bill.amount.toLocaleString()}
                      </span>
                      <span className="text-xs" style={{
                    color: OPENFINANCE_THEME.colors.textMuted
                  }} data-id="element-332">
                        {bill.dueDate}
                      </span>
                    </div>

                    {!bill.hasVRP && <div className="text-xs mt-2" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-333">
                        Tap to set up automatic payments
                      </div>}
                  </div>

                  {/* Arrow */}
                  <ArrowRightIcon className="w-5 h-5 flex-shrink-0 mt-2" style={{
                color: isRecommended ? OPENFINANCE_THEME.colors.accent500 : OPENFINANCE_THEME.colors.textMuted
              }} data-id="element-334" />
                </div>
              </motion.button>;
        })}
        </motion.div>

        {/* Info Note */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="p-3 rounded-lg" style={{
        backgroundColor: OPENFINANCE_THEME.colors.primary50,
        border: `1px solid ${OPENFINANCE_THEME.colors.primary200}`
      }} data-id="element-335">
          <p className="text-xs" style={{
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-336">
            💡 <span className="font-semibold" data-id="element-337">For this demo:</span> We
            recommend setting up auto-pay for{' '}
            <span className="font-semibold" data-id="element-338">
              {recommendedBill?.merchantName}
            </span>{' '}
            to see the complete VRP authorization flow.
          </p>
        </motion.div>

        {/* Cancel Button */}
        {onCancel && <button onClick={onCancel} className="w-full px-6 py-3 rounded-xl font-semibold transition-all border-2 hover:bg-gray-50" style={{
        borderColor: OPENFINANCE_THEME.colors.border,
        color: OPENFINANCE_THEME.colors.text
      }} data-id="element-339">
            Back to Landing
          </button>}
      </div>
    </AnimatedScreen>;
}